<script setup lang="ts">
import { BibAssignSort, PostResponse } from '@/objects/generated';
import BaseObjListView from '../BaseObjListView.vue';
import { ClickIconRenderer, ValidatedInt } from '@/components';
import type BaseObjListViewCompExpose from '@/objects/BaseObjListViewCompExpose';
import { ref } from 'vue';
import { useGuiStore } from '@/stores';
import BibAssignSortSourceRenderer from './BibAssignSortSourceRenderer.vue';
import BibAssignSortSourceEditor from './BibAssignSortSourceEditor.vue';
import { swapInList } from '@/utilities/utils';
import { GridColumnOptions } from '@/components/Grid/Classes';
import { DropdownItemButton } from '@/objects';
import { BaseObjDao } from '@/dao/BaseObjDao.ts';

const bibAssignSort = defineModel<Array<BibAssignSort>>({ required: true });

const guiStore = useGuiStore();

const moveup = (sort: number) => {
    const clicked = bibAssignSort.value.find((x) => x.Sort == sort);
    const other = swapInList(
        bibAssignSort.value,
        clicked!,
        (x: BibAssignSort) => x.Sort < sort && x.EntityState != 'Deleted',
        (selected: BibAssignSort, current: BibAssignSort) => (current.Sort > selected.Sort ? current : selected),
        (e: BibAssignSort) => e.Sort,
        (e: BibAssignSort, v: number) => (e.Sort = v),
    );

    if (other == null) return;

    sortlist.value?.dataUpdated();
};

const movedown = (sort: number) => {
    const clicked = bibAssignSort.value.find((x) => x.Sort == sort);
    const other = swapInList(
        bibAssignSort.value,
        clicked!,
        (x: BibAssignSort) => x.Sort > sort && x.EntityState != 'Deleted',
        (selected: BibAssignSort, current: BibAssignSort) => (current.Sort < selected.Sort ? current : selected),
        (e: BibAssignSort) => e.Sort,
        (e: BibAssignSort, v: number) => (e.Sort = v),
    );

    if (other == null) return;

    sortlist.value?.dataUpdated();
};

const deleteSort = (id: number) => {
    const clicked = bibAssignSort.value.find((x) => x.ID == id);
    if (clicked?.EntityState === 'Added') {
        bibAssignSort.value = bibAssignSort.value.filter((x) => x.ID != id);
    } else {
        clicked!.Delete();
    }
    sortlist.value?.dataUpdated();
    emit('datachanged');
};

const addCustomColumns = (columns: GridColumnOptions<BibAssignSort>[]) => {
    return [
        new GridColumnOptions<BibAssignSort>({
            sortable: false,
            filterable: false,
            editable: false,
            dataField: 'Sort',
            cellRenderer: ValidatedInt,
        }),
        new GridColumnOptions<BibAssignSort>({
            sortable: false,
            filterable: false,
            editable: false,
            dataField: 'Sort',
            cellRenderer: ClickIconRenderer,
            cellRendererParams: { action: movedown, icon: 'fa-down' },
            maxWidth: 35,
            minWidth: 35,
        }),
        new GridColumnOptions<BibAssignSort>({
            sortable: false,
            filterable: false,
            editable: false,
            dataField: 'Sort',
            cellRenderer: ClickIconRenderer,
            cellRendererParams: { action: moveup, icon: 'fa-up' },
            maxWidth: 35,
            minWidth: 35,
        }),
        ...columns,
        new GridColumnOptions<BibAssignSort>({
            columnHeaderText: 'bibassignsort.source',
            sortable: false,
            filterable: false,
            dataField: 'NONE',
            valueGetter: (params: BibAssignSort) => {
                if (params.SortType == 'Result') {
                    return params.ResultID;
                }
                if (params.SortType == 'Series') {
                    return params.SeriesID;
                }
                if (params.SortType == 'SeedingList') {
                    return params.SeedingListID;
                }
                return null;
            },
            valueSetter: (params: BibAssignSort, value: number) => {
                if (params.SortType == 'Result') {
                    params.ResultID = value;
                }
                if (params.SortType == 'Series') {
                    params.SeriesID = value;
                }
                if (params.SortType == 'SeedingList') {
                    params.SeedingListID = value;
                }
            },
            cellEditorParams: { stopEditing: () => sortlist.value?.stopEditing() },
            cellEditor: BibAssignSortSourceEditor,
            cellRenderer: BibAssignSortSourceRenderer,
        }),
        new GridColumnOptions<BibAssignSort>({
            sortable: false,
            filterable: false,
            editable: false,
            dataField: 'ID',
            cellRenderer: ClickIconRenderer,
            cellRendererParams: { action: deleteSort, icon: 'fa-trash-xmark' },
            maxWidth: 35,
            minWidth: 35,
        }),
    ];
};

const itemAdded = (item: BibAssignSort) => {
    item.Sort = bibAssignSort.value.reduce((max, current) => (current.Sort > max ? current.Sort : max), 0) + 1;
    return item;
};

const sortlist = ref<BaseObjListViewCompExpose<BibAssignSort> | null>(null);

const quickButtons = [
    DropdownItemButton.create({
        action: () => {
            sortlist.value?.addItem();
        },
        buttonType: 'Secondary',
        icon: 'fa-grid-2-plus',
        translationkey: 'button.addingrid',
    }),
    DropdownItemButton.create({
        action: async () => {
            await sortlist.value?.saveList();
        },
        buttonType: 'Save',
        translationkey: 'general.save',
    }),
];

const emit = defineEmits<{
    (e: 'datachanged'): void;
}>();

defineExpose({
    shown() {
        sortlist.value?.resizeColumns();
    },
    saveList: async (): Promise<PostResponse | null> => {
        return sortlist.value?.saveList() ?? null;
    },
});
</script>
<template>
    <div>
        <BaseObjListView
            :baseType="BibAssignSort"
            :gui="guiStore.BibAssignSort"
            v-model="bibAssignSort"
            :hideDefaultButtons="true"
            :customColumns="addCustomColumns"
            :sort="(a: BibAssignSort, b: BibAssignSort) => (a.Sort > b.Sort ? 1 : -1)"
            :filter="[(b: BibAssignSort) => b.EntityState != 'Deleted']"
            ref="sortlist"
            @itemAdded="itemAdded"
            :loadOnMount="false"
            :hideSearchField="true"
            :quickButtons="quickButtons"
            @datachanged="emit('datachanged')"
        />
    </div>
</template>
